import React, { useEffect, useState, useRef } from 'react'
import * as ko from 'knockout'
import * as R from 'ramda'
import { SurveyPDF, FlatRepository, FlatTextbox } from 'survey-pdf'
import { ignitionDownload } from '~/utils/ignitionFetch'
import download from 'downloadjs'
import * as Analytics from '~/helpers/analytics'

import { Box, Card, Heading, Inline, Loading, Modal, Button, Stack, Text } from '@myxplor/stardust'

import CloseIcon from '~/icons/CloseIcon'
import { SurveyJSCreator } from '~/components/PreStyled'
import { ignitionPost } from '~/utils/ignitionFetch'
import { sanitiseHtml } from '~/utils/sanitiseHtml'
import useSchema from '~/utils/useSchema'
import useSchemaHistory from '~/utils/useSchemaHistory'

const CustomSurveyEditor = ({ serviceId, token }) => {
  const [schema, getSchema] = useSchema()
  const [schemaHistory, getSchemaHistory] = useSchemaHistory()
  const [selectedVersionId, setSelectedVersionId] = useState()
  const valueInDropdown = useRef()
  const [warningModalDisplayed, setWarningModalDisplayed] = useState(false)

  useEffect(() => {
    setupAndRenderEditor()
  }, [schema, schemaHistory])

  useEffect(() => {
    getSchemaHistory(serviceId, token)
    if (selectedVersionId) {
      getSchema(serviceId, null, null, null, token, selectedVersionId)
    } else {
      getSchema(serviceId)
    }
  }, [selectedVersionId])

  const displayWarningModal = () => {
    return (
      <Modal
        width={0.5}
        backdrop={true}
        isOpen={warningModalDisplayed}
        onBlur={() => setWarningModalDisplayed(false)}
      >
        <Card>
          <Box padding="large" width="440px">
            <Box
              color="lowEmphasis"
              padding="small"
              position="absolute"
              right={0}
              top={0}
              onClick={() => setWarningModalDisplayed(false)}
            >
              <CloseIcon />
            </Box>
            <Stack space="small">
              <Heading level={6}>Load Previous Version</Heading>
              <Text>
                Loading a previous version of the enrolment form may result in a loss of unsaved data.
              </Text>
              <Text>Are you sure that you wish to proceed?</Text>
              <Inline align="right" space="medium">
                <Button variant="outline" color="secondary" onClick={() => setWarningModalDisplayed(false)}>
                  Cancel
                </Button>
                <Button
                  color="primary"
                  onClick={() => {
                    Analytics.pushLoadVersionEvent()
                    setWarningModalDisplayed(false)
                    valueInDropdown && setSelectedVersionId(valueInDropdown.current)
                  }}
                >
                  Proceed
                </Button>
              </Inline>
            </Stack>
          </Box>
        </Card>
      </Modal>
    )
  }

  // Pre-Set Editor options before creating editor
  const editorOptions = {
    // Toolbox (LHS Menu) Question Type options
    questionTypes: [
      'text',
      'checkbox',
      'radiogroup',
      'dropdown',
      'comment',
      'boolean',
      'multipletext',
      'matrix',
      'html'
    ],

    // RHS Panel - Set to "false" to hide from editor
    showPropertyGrid: false,

    // Tab above editor to show raw JSON Schema
    showJSONEditorTab: false, // True for Dev work, set to false in Prod

    allowControlSurveyTitleVisibility: false,
    showSimulatorInTestSurveyTab: false,
    designerHeight: '90vh'
  }

  const setElementAllowOperations = editor => {
    // Iterates through each Toolbox Element, and sets it to be uneditable
    editor.onElementAllowOperations.add((editor, options) => {
      // Get each element, and check it's 'type'
      const obj = options.obj

      const objType = obj.getType()

      if (!obj || !obj.page) {
        return
      }

      // if the object's 'type' is included in our allowed
      if (editorOptions.questionTypes.includes(objType)) {
        options.allowCopy = false
        options.allowShowHideTitle = false
        options.allowAddToToolbox = false
        options.allowChangeType = false
      }

      // apply these options to any question who's name starts with 'Xplor_'
      if (obj.name.startsWith('Xplor_')) {
        options.allowShowEditor = false
        options.allowShowHideTitle = false
        options.allowDelete = false
        options.allowChangeType = false
        options.allowCopy = false
        options.allowAddToToolbox = false
      }

      // Xplor Questions that are required and must stay that way
      if (obj.name.startsWith('XplorRequired_')) {
        options.allowShowEditor = false
        options.allowDelete = false
        options.allowChangeType = false
        options.allowChangeRequired = false
        options.allowCopy = false
        options.allowAddToToolbox = false
      }
    })

    // Pages with question names starting with Xplor_, cannot be deleted
    editor.onElementDeleting.add((editor, options) => {
      const elementTypeofPage = 2
      if (options.elementType === elementTypeofPage) {
        options.element.questions.forEach(question => {
          if (question.name.startsWith('Xplor_')) {
            options.allowing = false
          }
        })
      }
    })

    // Resets the title of a question back to the original "title" if it's deleted
    editor.onPropertyValueChanging.add((editor, options) => {
      if (options.propertyName === 'title' && options.newValue === null && options.value === '') {
        options.newValue = options.obj.title
      }
    })

    // hides isVisible, visibleif and questionsOrder properties on Pages with Xplor questions
    editor.onCanShowProperty.add((editor, options) => {
      if (options.obj.getType() === 'page') {
        options.obj.questions.forEach(question => {
          if (question.name.startsWith('Xplor_')) {
            const propName = options.property.name
            options.canShow =
              !propName.startsWith('visible') && propName != 'questionsOrder' && propName != 'visibleif'
          }
        })
      }
    })

    return editor
  }

  const savePdfCallback = editor => {
    const options = {
      orientation: 'p',
      fontSize: 12,
      margins: {
        left: 10,
        right: 10,
        top: 20,
        bot: 10
      },
      textFieldRenderAs: 'multiLine',
      htmlRenderAs: 'auto', // Render complex elements as image and simple ones as text
      commercial: true
    }

    const surveyPDF = new SurveyPDF(editor.text, options)
    surveyPDF.data = {} // try to pass in survey data to the export pdf
    surveyPDF.mode = 'display'

    FlatRepository.getInstance().register('dropdown', FlatTextbox)

    surveyPDF.onRenderHeader.add(async function(_, canvas) {
      if (canvas.pageNumber === 1) {
        canvas.drawText({
          text: 'Enrolment Form',
          fontSize: 16,
          horizontalAlign: 'center',
          isBold: true,
          margins: {
            top: 0
          }
        })
      }

      canvas.drawText({
        text:
          'This enrolment form is in accordance with the Education and Care Services National Regulations.',
        fontSize: 10,
        horizontalAlign: 'center',
        margins: {
          top: canvas.pageNumber === 1 ? 55 : 15
        }
      })
    })

    surveyPDF.onRenderFooter.add((_, canvas) => {
      canvas.drawText({
        text: 'Page ' + canvas.pageNumber + ' of ' + canvas.pageCount,
        fontSize: 10,
        horizontalAlign: 'right',
        verticalAlign: 'bottom',
        margins: {
          right: 12,
          bot: 5
        }
      })
    })
    surveyPDF.save(`${new Date().toJSON()} - enrolment.pdf`)
  }

  const setupUndo = editor => {
    editor.onAfterUndo.add(function(_sender, _options) {
      Analytics.pushUndoEvent()
    })

    return editor
  }

  const setupRedo = editor => {
    editor.onAfterRedo.add(function(_sender, _options) {
      Analytics.pushRedoEvent()
    })

    return editor
  }

  const setupExportToPDF = editor => {
    editor.toolbarItems.push({
      id: 'svd-save-pdf',
      title: 'Export PDF',
      visible: true,
      tooltip: 'Save PDF',
      component: 'svd-button',
      action: () => {
        const editorClone = Object.assign({}, editor)
        const cleanedContent = sanitiseHtml(editor.text)

        editorClone.text = cleanedContent

        Analytics.pushExportPDFEvent()
        savePdfCallback(editorClone)
      }
    })

    return editor
  }

  const saveEnrolmentCSVCallback = () => {
    ignitionDownload(`api/v1/enrolment-form/service/${serviceId}/report`, token, {}).then(response => {
      response.text().then(data => {
        download(data, 'custom-enrolment-report.csv', 'text/csv')
      })
    })
  }

  const saveSchemaCSVCallback = () => {
    ignitionDownload(`api/v1/form_schemas/service/${serviceId}/report`, token, {}).then(response => {
      response.text().then(data => {
        download(data, 'custom-field-details-report.csv', 'text/csv')
      })
    })
  }

  const setupExportToCSV = editor => {
    editor.toolbarItems.push({
      id: 'svd-save-csv',
      title: 'Custom Enrolment Report',
      visible: true,
      tooltip: 'Save CSV',
      component: 'svd-button',
      action: () => {
        Analytics.pushExportCustomEnrolmentReportEvent()
        saveEnrolmentCSVCallback()
      }
    })

    editor.toolbarItems.push({
      id: 'svd-save-csv',
      title: 'Custom Field Details Report',
      visible: true,
      tooltip: 'Save CSV',
      component: 'svd-button',
      action: () => {
        Analytics.pushExportCustomDetailsReportEvent()
        saveSchemaCSVCallback()
      }
    })

    return editor
  }

  function locateAdorner(adornerName, itemsArray) {
    var adornerIndex = -1
    for (var i = 0; i < itemsArray.length; i++) {
      if (itemsArray[i].name === adornerName) {
        adornerIndex = i
        break
      }
    }

    return adornerIndex
  }

  const setupHideFields = editor => {
    editor.onDefineElementMenuItems.add((_sender, options) => {
      const unHideableFields = [
        'XplorRequired_ChildFirstName',
        'XplorRequired_ChildLastName',
        'Xplor_ChildDOB',
        'Xplor_ChildCRN',
        'Xplor_ChildAddress',
        'XplorRequired_IntendedStartDate',
        'XplorRequired_PCFirstName',
        'XplorRequired_PCLastName',
        'Xplor_PCCRN',
        'Xplor_PCAddress',
        'XplorRequired_PCPhone',
        'XplorRequired_PCEmail',
        'XplorRequired_Consents_v2',
        'Declarations',
        'XplorRequired_EddrCheck',
        'Xplor_Captcha'
      ]

      const unHideablePanels = [
        'Xplor_ChildDetailsPanel',
        'Xplor_BookingDetailsPanel',
        'Xplor_PrimaryCarerPanel'
      ]

      var question = options.obj

      var isVisible = ko.observable(question.visible)
      var adornerIndex = locateAdorner('isrequired', options.items)

      if (question.allowingOptions.allowChangeRequired) {
        var isRequired = ko.observable(question.isRequired)

        var requiredToggleAdorner = {
          id: 'requiredAdorner',
          title: 'RequiredTitle',
          text: 'Is required?',
          name: 'isrequired',
          icon: ko.computed(() => (isRequired() ? 'icon-actionisrequired' : 'icon-actionnotrequired')),
          onClick: () => {
            const newRequired = !question.isRequired
            question.isRequired = newRequired
            isRequired(newRequired)
            newRequired ? Analytics.pushMakeRequiredFieldEvent() : Analytics.pushMakeUnrequiredFieldEvent()
          }
        }

        // Replace the existing adorner with the custom one.
        if (adornerIndex > -1) {
          options.items.splice(adornerIndex, 1, requiredToggleAdorner)
        }
      }

      var visibleToggleAdorner = {
        id: 'visibleAdorner',
        title: 'VisibleTitle',
        text: 'Is visible?',
        // these icons are existing surveryjs icons usually used for show/hide a field's title, which I'm repurposing here
        // icon is a static field so there is no normal way to make it update/rerender when visibility changes
        // so I'm hooking into the SurveyJS's under-the-hood knockoutJS to a rerender
        // code stolen from: https://github.com/surveyjs/survey-creator/blob/407f8ee5c798b7b9875cb27f4d8e5fc18b0c9d58/packages/survey-creator/src/editor.ts#L1814-L1838
        icon: ko.computed(() => (isVisible() ? 'icon-actionshowtitle' : 'icon-actionhidetitle')),
        onClick: () => {
          const newVisibility = !question.visible
          question.visible = newVisibility
          isVisible(newVisibility)

          newVisibility ? Analytics.pushMakeVisibleEvent() : Analytics.pushMakeNotVisibleEvent()
        }
      }

      // NOTE: The Adorner doesn't actually get added unless we add it onto the options.items array
      // So we can use `var question` to determine if we want to add/exclude the Adorner from any question

      // Only add visibleToggleAdorner if it is a "Hideable" field or panel and if it is not dependent on another field
      if (
        !unHideableFields.includes(question.name) &&
        !unHideablePanels.includes(question.name) &&
        !question.enableIf
      ) {
        // if found the index, insert the visibleToggleAdorner next to it
        if (adornerIndex > -1) {
          options.items.splice(adornerIndex, 0, visibleToggleAdorner)
        }
        // otherwise push the Adorner onto the end of the list
        else {
          options.items.push(visibleToggleAdorner)
        }
      }
    })

    return editor
  }

  const selectVersionCallback = () => {
    setWarningModalDisplayed(true)
  }

  const setupVersionHistory = editor => {
    editor.toolbarItems.push(
      {
        id: 'svd-version-history',
        visible: true,
        component: 'svd-dropdown',
        items: schemaHistory.data,
        // Reference https://github.com/surveyjs/survey-creator/blob/master/packages/survey-creator-core/src/components/simulator.ts
        action: ko.computed({
          read: () => valueInDropdown.current,
          write: val => (valueInDropdown.current = val)
        })
      },
      {
        id: 'svd-version-history-submit',
        visible: true,
        component: 'svd-button',
        title: 'Load Version',
        action: selectVersionCallback
      }
    )
  }

  const setupToolbarItems = editor => {
    const surveySettingsButton = R.find(R.propEq('id', 'svd-survey-settings'), editor.toolbarItems())

    editor.toolbarItems.remove(surveySettingsButton)

    setupUndo(editor)
    setupRedo(editor)
    setupExportToPDF(editor)
    setupVersionHistory(editor)
    setupExportToCSV(editor)

    return editor
  }

  const setupToolboxItems = editor => {
    editor.onQuestionAdded.add(function(_sender, options) {
      switch (options.question.getType()) {
        case 'text':
          Analytics.pushDragDropSingleInputEvent()
          break
        case 'checkbox':
          Analytics.pushDragDropCheckboxEvent()
          break
        case 'radiogroup':
          Analytics.pushDragDropRadiogroupEvent()
          break
        case 'dropdown':
          Analytics.pushDragDropDropdownEvent()
          break
        case 'comment':
          Analytics.pushDragDropCommentEvent()
          break
        case 'boolean':
          Analytics.pushDragDropBooleanEvent()
          break
        case 'html':
          Analytics.pushDragDropHtmlEvent()
          break
        case 'multipletext':
          Analytics.pushDragDropMultipleTextEvent()
          break
        case 'matrix':
          Analytics.pushDragDropMatrixEvent()
          break
      }
    })

    return editor
  }

  const postSchema = (serviceId, schema, token) => {
    const body = {
      schema: schema
    }
    ignitionPost(`api/v1/form_schemas/service/${serviceId}`, token, body)
      .then(() => {
        alert('Custom form updated successfully.')
        setSelectedVersionId(null)
      })
      .catch(res => {
        alert(res.errors ? res.errors : 'An error occurred while updating your custom form.')
      })
  }

  const setupLoadAndSave = editor => {
    // Load the existing schema from the useContext
    if (schema.data) {
      editor.text = JSON.stringify(schema.data.schema)
    }

    // Setup the save function to store the data to the context
    editor.saveSurveyFunc = (saveNo, callback) => {
      postSchema(serviceId, JSON.parse(editor.text), token)
      !!callback && callback(saveNo, true)

      Analytics.pushSaveEvent()
    }

    return editor
  }

  const setupElement = editor => {
    editor.onModified.add(function(_sender, options) {
      if (
        options.type === 'PROPERTY_CHANGED' &&
        options.name === 'title' &&
        options.oldValue !== options.newValue
      ) {
        Analytics.pushEditQuestionEvent()
      }
    })

    return editor
  }

  const setupPageControls = editor => {
    editor.onPageAdded.add(function(_sender, _options) {
      Analytics.pushAddNewPageEvent()
    })

    return editor
  }

  // Main pipeline function to setup and render Editor Options
  const setupAndRenderEditor = () => {
    // disables in-place checkbox/radiogroup/dropdown item add/edit options,
    // still allows edit via the edit dialog
    SurveyJSCreator.removeAdorners(['choices-label', 'select-choices', 'choices-draggable'])

    // Create 'editor' object and Render the object to "surveyEditorContainer" ID
    const editor = new SurveyJSCreator.SurveyCreator('surveyCreatorContainer', editorOptions)

    // Pipe editor through each of our setup functions
    R.pipe(
      setupToolbarItems,
      setupToolboxItems,
      setupPageControls,
      setElementAllowOperations, // First, set all the 'ToolBox' Element settings
      setupHideFields,
      setupLoadAndSave, // Last, set the "save" and "load" logic
      setupElement
    )(editor)
  }

  return (
    <>
      {(schema.loading || schemaHistory.loading) && <Loading />}
      {schema.error && (
        <Box display="flex" justifyContent="center" padding="small">
          <Box width={1 / 2}>{schema.error}</Box>
        </Box>
      )}
      {schemaHistory.error && (
        <Box display="flex" justifyContent="center" padding="small">
          <Box width={1 / 2}>{schemaHistory.error}</Box>
        </Box>
      )}
      {displayWarningModal()}
      {schema.data && schemaHistory.data && <div id="surveyCreatorContainer" />}
    </>
  )
}

export default CustomSurveyEditor
